import { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useDropzone } from 'react-dropzone';
import  { XMarkIcon, PhotoIcon, PlusIcon, TrashIcon, QrCodeIcon } from '@heroicons/react/24/outline';
import { Button, Input, Switch } from '@common/components/ui';
import productService from '@services/productService';
import mediaService from '@services/mediaService';
import categoryService from '@services/categoryService';
import { Product, Category, ProductVariant } from '@services/core/types';
import { useDebounce } from '../../hooks/useDebounce';
import toast from 'react-hot-toast';
import Lightbox from 'yet-another-react-lightbox';
import 'yet-another-react-lightbox/styles.css';
import axios from 'axios';
import { BarcodeScannerModal } from './BarcodeScannerModal';

// Predefined variant options
const PREDEFINED_VARIANT_OPTIONS = {
  Size: ['XS', 'S', 'M', 'L', 'XL', 'XXL'],
  SizeNumber: ['24', '26', '28', '30', '32', '34', '36', '38', '40'],
  Color: ['Red', 'Blue', 'Green', 'Black', 'White', 'Gray', 'Pink', 'Purple', 'Yellow', 'Orange'],
  Material: ['Cotton', 'Polyester', 'Wool', 'Silk', 'Linen', 'Denim'],
  Style: ['Regular', 'Slim', 'Loose', 'Fitted'],
};

interface ProductFormProps {
  product: Product | null;
  categories: Category[];
  onSuccess: () => void;
  onCancel: () => void;
}

const ProductForm = ({ product, categories, onSuccess, onCancel }: ProductFormProps) => {
  const queryClient = useQueryClient();
  
  // Form state
  const [title, setTitle] = useState('');
  const [sku, setSku] = useState('');
  const [barcode, setBarcode] = useState('');
  const [slug, setSlug] = useState('');
  const [description, setDescription] = useState('');
  const [shortDescription, setShortDescription] = useState('');
  const [price, setPrice] = useState('');
  const [compareAtPrice, setCompareAtPrice] = useState('');
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');
  const [quantity, setQuantity] = useState('0');
  const [trackQuantity, setTrackQuantity] = useState(true);
  const [allowBackorder, setAllowBackorder] = useState(false);
  const [status, setStatus] = useState<'active' | 'inactive' | 'draft'>('draft');
  const [featured, setFeatured] = useState(false);
  const [bestseller, setBestseller] = useState(false);

  // Variant state
  const [variants, setVariants] = useState<ProductVariant[]>([]);
  const [showVariantOptions, setShowVariantOptions] = useState(false);
  const [customVariantName, setCustomVariantName] = useState('');
  const [customVariantValues, setCustomVariantValues] = useState('');

  // Lightbox state
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  // Barcode scanner state
  const [isScannerOpen, setIsScannerOpen] = useState(false);
  const [loadingExternalProduct, setLoadingExternalProduct] = useState(false);

  // Image state
  const [images, setImages] = useState<File[]>([]);
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);
  const [existingImages, setExistingImages] = useState(product?.images || []);
  const [uploading, setUploading] = useState(false);

  // Slug validation
  const [slugChecked, setSlugChecked] = useState(false);
  const [slugAvailable, setSlugAvailable] = useState(true);
  const [slugMessage, setSlugMessage] = useState('');
  const debouncedSlug = useDebounce(slug, 300);

  // Populate form on edit
  useEffect(() => {
    if (product) {
      setTitle(product.title);
      setSku(product.sku);
      setBarcode(product.barcode || '');
      setSlug(product.slug);
      setDescription(product.description || '');
      setShortDescription(product.shortDescription || '');
      setPrice(String(product.price));
      setCompareAtPrice(product.compareAtPrice ? String(product.compareAtPrice) : '');
      setSelectedCategories(product.categories || []);
      setTags(product.tags || []);
      setQuantity(String(product.inventory?.quantity || 0));
      setTrackQuantity(product.inventory?.trackQuantity ?? true);
      setAllowBackorder(product.inventory?.allowBackorder ?? false);
      setStatus(product.status);
      setFeatured(product.metadata?.featured ?? false);
      setBestseller(product.metadata?.bestseller ?? false);
      setExistingImages(product.images || []);
      setVariants(product.variants || []);
    }
  }, [product]);

  // When variants exist, the product-level quantity is derived from them rather
  // than entered manually — keep it in sync whenever a variant is added, removed,
  // or its own quantity changes.
  useEffect(() => {
    if (variants.length > 0) {
      const total = variants.reduce((sum, v) => sum + (parseInt(String(v.inventory?.quantity)) || 0), 0);
      setQuantity(String(total));
    }
  }, [variants]);

  // Auto-generate slug from title
  useEffect(() => {
    if (!product && title) {
      const generatedSlug = title
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)+/g, '');
      setSlug(generatedSlug);
    }
  }, [title, product]);

  // Validate slug
  useEffect(() => {
    const checkSlug = async () => {
      if (!debouncedSlug) {
        setSlugChecked(false);
        setSlugMessage('');
        return;
      }

      setSlugMessage('Checking availability...');
      try {
        // Pass product ID to exclude current product when editing
        const available = await productService.checkSlugAvailability(debouncedSlug, product?.id);
        setSlugChecked(true);
        setSlugAvailable(available);
        setSlugMessage(available ? '✓ Available' : '✗ Already taken');
      } catch (error) {
        console.error('Error checking slug:', error);
        setSlugMessage('');
      }
    };

    checkSlug();
  }, [debouncedSlug, product?.id]);

  // Dropzone for images
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      'image/*': ['.png', '.jpg', '.jpeg', '.gif', '.webp'],
    },
    onDrop: (acceptedFiles) => {
      const newImages = [...images, ...acceptedFiles];
      setImages(newImages);

      // Create previews
      const newPreviews = acceptedFiles.map((file) => URL.createObjectURL(file));
      setImagePreviews([...imagePreviews, ...newPreviews]);
    },
  });

  // Remove image
  const handleRemoveImage = (index: number) => {
    const newImages = [...images];
    const newPreviews = [...imagePreviews];
    URL.revokeObjectURL(newPreviews[index]);
    newImages.splice(index, 1);
    newPreviews.splice(index, 1);
    setImages(newImages);
    setImagePreviews(newPreviews);
  };

  // Remove existing image
  const handleRemoveExistingImage = (index: number) => {
    const newExisting = [...existingImages];
    newExisting.splice(index, 1);
    setExistingImages(newExisting);
  };

  // Add tag
  const handleAddTag = () => {
    const trimmedInput = tagInput.trim();
    if (!trimmedInput) return;

    // Split by comma to handle multiple tags at once
    const newTags = trimmedInput
      .split(',')
      .map((tag) => tag.trim())
      .filter((tag) => tag && !tags.includes(tag)); // Filter out empty strings and duplicates

    if (newTags.length > 0) {
      setTags([...tags, ...newTags]);
      setTagInput('');
    }
  };

  // Handle tag input change with comma detection
  const handleTagInputChange = (value: string) => {
    // If input ends with a comma, process tags immediately
    if (value.endsWith(',')) {
      setTagInput(value);
      // Small timeout to ensure state is updated
      setTimeout(() => {
        handleAddTag();
      }, 0);
    } else {
      setTagInput(value);
    }
  };

  // Remove tag
  const handleRemoveTag = (tagToRemove: string) => {
    setTags(tags.filter((tag) => tag !== tagToRemove));
  };

  // Variant management functions
  const handleAddVariantOption = (optionName: string, predefinedValues?: string[]) => {
    if (!predefinedValues || predefinedValues.length === 0) return;

    // Create variants for each value
    const newVariants = predefinedValues.map((value, index) => ({
      id: `temp-${Date.now()}-${index}`,
      title: `${title} - ${value}`,
      sku: `${sku}-${value.toLowerCase().replace(/\s+/g, '-')}`,
      inventory: {
        trackQuantity: true,
        quantity: 0,
        allowBackorder: false,
      },
      options: [{ name: optionName, value: value }],
    }));

    // Remove existing variants with the same option name to avoid duplicates
    const filteredVariants = variants.filter(
      (variant) => !variant.options.some((opt) => opt.name === optionName)
    );

    setVariants([...filteredVariants, ...newVariants]);
    setShowVariantOptions(false);
  };

  const handleAddCustomVariant = () => {
    if (!customVariantName.trim() || !customVariantValues.trim()) {
      toast.error('Please enter both variant name and values');
      return;
    }

    const values = customVariantValues
      .split(',')
      .map((v) => v.trim())
      .filter((v) => v);

    if (values.length === 0) {
      toast.error('Please enter at least one variant value');
      return;
    }

    handleAddVariantOption(customVariantName, values);
    setCustomVariantName('');
    setCustomVariantValues('');
  };

  const handleRemoveVariant = (variantId: string) => {
    setVariants(variants.filter((v) => v.id !== variantId));
  };

  const handleVariantChange = (variantId: string, field: string, value: any) => {
    setVariants(
      variants.map((variant) => {
        if (variant.id === variantId) {
          if (field === 'inventory.quantity') {
            // Store raw value to allow clearing the field
            return {
              ...variant,
              inventory: {
                ...variant.inventory,
                quantity: value as any, // Allow string during editing, convert on submit
              },
            };
          }
          return { ...variant, [field]: value };
        }
        return variant;
      })
    );
  };

  // Toggle category
  const handleToggleCategory = (categoryId: string) => {
    if (selectedCategories.includes(categoryId)) {
      setSelectedCategories(selectedCategories.filter((id) => id !== categoryId));
    } else {
      setSelectedCategories([...selectedCategories, categoryId]);
    }
  };

  // Fetch external product data by barcode
  const fetchExternalProduct = async (barcode: string) => {
    setLoadingExternalProduct(true);
    try {
      const response = await axios.get(`https://finplus-ks.com/product/${barcode}`);
      const externalProduct = response.data;

      // Map external product data to form fields
      if (externalProduct) {
        // Set basic fields
        setTitle(externalProduct.name || '');
        setSku(externalProduct.code || '');
        setDescription(externalProduct.description || '');
        setPrice(externalProduct.finalPrice ? String(parseFloat(externalProduct.finalPrice)) : '');
        
        // Set the scanned barcode
        setBarcode(barcode);
        
        // Set quantity from qntLeft
        if (externalProduct.qntLeft) {
          const quantityValue = parseFloat(externalProduct.qntLeft);
          setQuantity(String(Math.floor(quantityValue))); // Convert to integer
        }
        
        // Set barcode as a tag
        if (externalProduct.barcode && !tags.includes(externalProduct.barcode)) {
          setTags([...tags, externalProduct.barcode]);
        }

        // Handle category - create if doesn't exist
        if (externalProduct.category && externalProduct.category.id) {
          const externalCategoryId = externalProduct.category.id;
          let matchedCategory = categories.find(
            (cat: any) => cat.externalId === externalCategoryId
          );

          // If category doesn't exist in local list, check if it exists in database
          if (!matchedCategory) {
            try {
              // Try to fetch all categories to check if one with this externalId exists
              const allCategoriesResponse = await categoryService.getAll();
              const allCategories = allCategoriesResponse.data.categories || allCategoriesResponse.data.items || [];
              
              // Flatten if needed and search for matching externalId or slug
              const flattenCategories = (cats: any[]): any[] => {
                const result: any[] = [];
                cats.forEach(cat => {
                  result.push(cat);
                  if (cat.children && cat.children.length > 0) {
                    result.push(...flattenCategories(cat.children));
                  }
                });
                return result;
              };
              
              const flatCategories = flattenCategories(allCategories);
              
              // First try to find by externalId
              matchedCategory = flatCategories.find(
                (cat: any) => cat.externalId === externalCategoryId
              );
              
              // If not found by externalId, check by slug
              const categorySlug = (externalProduct.category.name_en || externalProduct.category.name || 'imported-category')
                .toLowerCase()
                .replace(/[^a-z0-9]+/g, '-')
                .replace(/(^-|-$)+/g, '');
              
              const categoryBySlug = flatCategories.find(
                (cat: any) => cat.slug === categorySlug
              );
              
              // If found by slug but no externalId, update it
              if (!matchedCategory && categoryBySlug) {
                try {
                  const updateResponse = await categoryService.update(categoryBySlug.id, {
                    externalId: externalCategoryId,
                  });
                  matchedCategory = updateResponse.data;
                  toast.success(`Category "${matchedCategory.name_en}" updated with external ID and linked!`);
                } catch (updateError) {
                  console.error('Error updating category with externalId:', updateError);
                  // If update fails, still use the category
                  matchedCategory = categoryBySlug;
                  if (matchedCategory) {
                    toast.success(`Category "${matchedCategory.name_en}" found and linked!`);
                  }
                }
              }
              // If still not found, create it with unique slug
              else if (!matchedCategory) {
                let uniqueSlug = categorySlug;
                let slugExists = flatCategories.some((cat: any) => cat.slug === uniqueSlug);
                let attemptCount = 1;
                
                // Make slug unique if it already exists
                while (slugExists && attemptCount < 100) {
                  uniqueSlug = `${categorySlug}-${attemptCount}`;
                  slugExists = flatCategories.some((cat: any) => cat.slug === uniqueSlug);
                  attemptCount++;
                }
                
                const newCategoryData = {
                  name_en: externalProduct.category.name_en || externalProduct.category.name || 'Imported Category',
                  name_tr: externalProduct.category.name_tr || externalProduct.category.name || 'Imported Category',
                  name_sq: externalProduct.category.name_sq || externalProduct.category.name || 'Imported Category',
                  name_bs: externalProduct.category.name_bs || externalProduct.category.name || 'Imported Category',
                  slug: uniqueSlug,
                  externalId: externalCategoryId,
                  description: '',
                  sidebarOrder: 0,
                  heroOrder: 0,
                };

                const createResponse = await categoryService.create(newCategoryData);
                matchedCategory = createResponse.data;
                
                toast.success(`Category "${matchedCategory.name_en}" created and linked!`);
              } else if (matchedCategory) {
                toast.success(`Category "${matchedCategory.name_en}" found and linked!`);
              }
              
              // Refresh categories queries
              await queryClient.invalidateQueries({ queryKey: ['categories-tree'] });
              await queryClient.invalidateQueries({ queryKey: ['categories-parents'] });
              await queryClient.invalidateQueries({ queryKey: ['categories'] });
              
              // Wait a bit for queries to refetch
              await new Promise(resolve => setTimeout(resolve, 100));
              
            } catch (error: any) {
              console.error('Error handling category:', error);
              toast.error('Failed to link category, but product data was loaded');
            }
          }

          // Link category to product
          if (matchedCategory && !selectedCategories.includes(matchedCategory.id)) {
            setSelectedCategories([...selectedCategories, matchedCategory.id]);
          }
        }

        // Set status to draft by default for imported products
        setStatus('draft');

        toast.success('Product data loaded from external source!');
      }
    } catch (error: any) {
      console.error('Error fetching external product:', error);
      toast.error(error.response?.data?.message || 'Failed to fetch product data');
    } finally {
      setLoadingExternalProduct(false);
    }
  };

  // Handle barcode scan
  const handleBarcodeScanned = async (barcode: string) => {
    await fetchExternalProduct(barcode);
  };

  // Create mutation
  const createMutation = useMutation({
    mutationFn: async (data: any) => {
      const response = await productService.create(data);
      return response.data;
    },
    onSuccess: (createdProduct) => {
      toast.success('Product created successfully');
      // Upload images if any
      if (images.length > 0) {
        uploadImages(createdProduct.id);
      } else {
        onSuccess();
      }
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to create product');
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: any }) => {
      const response = await productService.update(id, data);
      return response.data;
    },
    onSuccess: (updatedProduct) => {
      toast.success('Product updated successfully');
      // Upload new images if any
      if (images.length > 0) {
        uploadImages(updatedProduct.id);
      } else {
        onSuccess();
      }
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to update product');
    },
  });

  // Upload images
  const uploadImages = async (productId: string) => {
    try {
      setUploading(true);
      
      const uploadedMedia = await mediaService.uploadImages(images, 'product', productId);
      
      // Convert uploaded media to ProductImage format matching backend DTO
      const newProductImages = uploadedMedia.map((media, index) => ({
        id: media.id || `media-${Date.now()}-${index}`,
        src: media.url,
        alt: media.alt || media.originalName || '',
        width: media.width || 0,
        height: media.height || 0,
        position: existingImages.length + index,
      }));
      
      // Get fresh product data to ensure we have the latest state
      const currentProduct = await productService.getOne(productId);
      const currentImages = currentProduct.data.images || [];
      
      // Merge current images with new uploads
      const updatedImages = [...currentImages, ...newProductImages];
      
      // Update product with complete images array
      await productService.update(productId, { images: updatedImages });
      
      toast.success('Images uploaded successfully');
      onSuccess();
    } catch (error: any) {
      console.error('Error uploading images:', error);
      console.error('Error details:', error.response?.data);
      toast.error(error.response?.data?.message || 'Failed to upload images');
    } finally {
      setUploading(false);
    }
  };

  // Handle submit
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!slugChecked || !slugAvailable) {
      toast.error('Please ensure the slug is valid and available');
      return;
    }

    // Normalize existing images to match backend DTO format
    const normalizedImages = existingImages.map((img, index) => ({
      id: img.id || `img-${index}`,
      src: img.src,
      alt: img.alt || '',
      width: img.width || 0,
      height: img.height || 0,
      position: img.position !== undefined ? img.position : index,
    }));

    const productData = {
      sku,
      slug,
      title,
      description,
      shortDescription,
      price: parseFloat(price),
      compareAtPrice: compareAtPrice ? parseFloat(compareAtPrice) : undefined,
      currency: 'EUR',
      barcode: barcode || undefined,
      images: normalizedImages,
      variants: variants.map(v => ({
        ...v,
        inventory: {
          ...v.inventory,
          quantity: parseInt(String(v.inventory.quantity)) || 0, // Convert to number
        },
      })),
      categories: selectedCategories,
      tags,
      inventory: {
        trackQuantity,
        quantity: parseInt(quantity),
        allowBackorder,
      },
      metadata: {
        featured,
        bestseller,
      },
      status,
    };

    if (product) {
      updateMutation.mutate({ id: product.id, data: productData });
    } else {
      createMutation.mutate(productData);
    }
  };

  const isLoading = createMutation.isPending || updateMutation.isPending || uploading;

  return (
    <form onSubmit={handleSubmit} className="space-y-6 p-6">
      {/* Basic Information */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">
            Basic Information
          </h3>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setIsScannerOpen(true)}
            icon={<QrCodeIcon className="h-5 w-5" />}
            disabled={loadingExternalProduct}
          >
            {loadingExternalProduct ? 'Loading...' : 'Scan Product'}
          </Button>
        </div>

        <Input
          label="Product Title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Enter product title"
          required
        />

        <Input
          label="SKU"
          value={sku}
          onChange={(e) => setSku(e.target.value)}
          placeholder="Enter product SKU"
          required
        />

        <Input
          label="Barcode (Optional)"
          value={barcode}
          onChange={(e) => setBarcode(e.target.value)}
          placeholder="Enter product barcode"
        />

        <div>
          <Input
            label="Slug"
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            placeholder="product-slug"
            required
          />
          {slugMessage && (
            <p
              className={`mt-1 text-sm ${
                slugAvailable ? 'text-green-600' : 'text-red-600'
              }`}
            >
              {slugMessage}
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Description
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
            placeholder="Enter product description"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Short Description
          </label>
          <textarea
            value={shortDescription}
            onChange={(e) => setShortDescription(e.target.value)}
            rows={2}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
            placeholder="Enter short description"
          />
        </div>
      </div>

      {/* Pricing */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white">Pricing</h3>

        <div className="grid grid-cols-2 gap-4">
          <Input
            label="Price (EUR)"
            type="number"
            step="0.01"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            placeholder="0.00"
            required
          />

          <Input
            label="Compare at Price (EUR)"
            type="number"
            step="0.01"
            value={compareAtPrice}
            onChange={(e) => setCompareAtPrice(e.target.value)}
            placeholder="0.00"
          />
        </div>
      </div>

      {/* Images */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">Product Images</h3>
          {(existingImages.length > 0 || imagePreviews.length > 0) && (
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {existingImages.length + imagePreviews.length} image{existingImages.length + imagePreviews.length !== 1 ? 's' : ''}
            </span>
          )}
        </div>

        {/* Combined images display */}
        {(existingImages.length > 0 || imagePreviews.length > 0) && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 mb-4">
            {/* Existing images */}
            {existingImages.map((img, index) => (
              <div
                key={`existing-${index}`}
                className="relative bg-white dark:bg-gray-800 rounded-lg border-2 border-gray-200 dark:border-gray-700 overflow-hidden group"
              >
                <div className="aspect-square relative">
                  <img
                    src={img.src}
                    alt={img.alt || 'Product'}
                    className="w-full h-full object-cover cursor-pointer hover:scale-105 transition-transform duration-200"
                    onClick={() => {
                      setLightboxIndex(index);
                      setLightboxOpen(true);
                    }}
                  />
                  {index === 0 && (
                    <div className="absolute top-2 left-2">
                      <span className="px-2 py-1 bg-indigo-600 text-white text-xs font-medium rounded">
                        Primary
                      </span>
                    </div>
                  )}
                </div>
                <div className="p-2 flex items-center justify-between bg-gray-50 dark:bg-gray-900">
                  <span className="text-xs text-gray-600 dark:text-gray-400 truncate flex-1">
                    Image {index + 1}
                  </span>
                  <button
                    type="button"
                    onClick={() => handleRemoveExistingImage(index)}
                    className="ml-2 p-1.5 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded hover:bg-red-200 dark:hover:bg-red-900/50 transition-colors"
                    title="Remove image"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}

            {/* New images preview */}
            {imagePreviews.map((preview, index) => (
              <div
                key={`new-${index}`}
                className="relative bg-white dark:bg-gray-800 rounded-lg border-2 border-green-300 dark:border-green-700 overflow-hidden group"
              >
                <div className="aspect-square relative">
                  <img
                    src={preview}
                    alt={`New image ${index + 1}`}
                    className="w-full h-full object-cover cursor-pointer hover:scale-105 transition-transform duration-200"
                    onClick={() => {
                      setLightboxIndex(existingImages.length + index);
                      setLightboxOpen(true);
                    }}
                  />
                  <div className="absolute top-2 left-2">
                    <span className="px-2 py-1 bg-green-600 text-white text-xs font-medium rounded">
                      New
                    </span>
                  </div>
                </div>
                <div className="p-2 flex items-center justify-between bg-green-50 dark:bg-green-900/20">
                  <span className="text-xs text-gray-600 dark:text-gray-400 truncate flex-1" title={images[index]?.name}>
                    {images[index]?.name || `Preview ${index + 1}`}
                  </span>
                  <button
                    type="button"
                    onClick={() => handleRemoveImage(index)}
                    className="ml-2 p-1.5 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded hover:bg-red-200 dark:hover:bg-red-900/50 transition-colors"
                    title="Remove image"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Dropzone */}
        <div
          {...getRootProps()}
          className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
            isDragActive
              ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20'
              : 'border-gray-300 dark:border-gray-600 hover:border-indigo-400 dark:hover:border-indigo-500'
          }`}
        >
          <input {...getInputProps()} />
          <PhotoIcon className="mx-auto h-12 w-12 text-gray-400 mb-3" />
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
            {isDragActive ? 'Drop the images here' : 'Upload Product Images'}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Drag & drop or click to browse • PNG, JPG, GIF up to 10MB
          </p>
        </div>
      </div>

      {/* Categories */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">
            Categories
          </h3>
          {selectedCategories.length > 0 && (
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {selectedCategories.length} selected
            </span>
          )}
        </div>
        {!categories || categories.length === 0 ? (
          <div className="space-y-2 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
            <p className="text-sm text-gray-700 dark:text-gray-300">
              No categories available. Please create categories first in the Categories page.
            </p>
            
          </div>
        ) : (
          <div className="space-y-2 max-h-48 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg p-2">
            {categories.map((category) => (
              <label
                key={category.id}
                className="flex items-center space-x-2 p-2 hover:bg-gray-50 dark:hover:bg-gray-700 rounded cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={selectedCategories.includes(category.id)}
                  onChange={() => handleToggleCategory(category.id)}
                  className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  {category.name_en}
                  {category.parentId && (
                    <span className="text-xs text-gray-500 dark:text-gray-400 ml-1">
                      (subcategory)
                    </span>
                  )}
                </span>
              </label>
            ))}
          </div>
        )}
      </div>

      {/* Tags */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white">Tags</h3>
        <div className="flex gap-2">
          <Input
            value={tagInput}
            onChange={(e) => handleTagInputChange(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleAddTag();
              }
            }}
            placeholder="Add tags (separate with commas)"
          />
          <Button type="button" onClick={handleAddTag}>
            Add
          </Button>
        </div>
        <div className="flex flex-wrap gap-2">
          {tags.map((tag) => (
            <span
              key={tag}
              className="inline-flex items-center gap-1 px-3 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-full text-sm"
            >
              {tag}
              <button
                type="button"
                onClick={() => handleRemoveTag(tag)}
                className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-200"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            </span>
          ))}
        </div>
      </div>

      {/* Inventory */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white">Inventory</h3>

        <Switch
          label="Track Quantity"
          checked={trackQuantity}
          onChange={(e) => setTrackQuantity(e.target.checked)}
        />

        <Input
          label="Quantity"
          type="number"
          value={quantity}
          onChange={(e) => setQuantity(e.target.value)}
          disabled={!trackQuantity && variants.length === 0}
          readOnly={variants.length > 0}
          helperText={variants.length > 0 ? 'Calculated automatically from variant quantities' : undefined}
        />

        <Switch
          label="Allow Backorder"
          checked={allowBackorder}
          onChange={(e) => setAllowBackorder(e.target.checked)}
        />
      </div>

      {/* Product Variants */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">
            Product Variations
          </h3>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setShowVariantOptions(!showVariantOptions)}
          >
            <PlusIcon className="h-4 w-4 mr-1" />
            Add Variation
          </Button>
        </div>

        {showVariantOptions && (
          <div className="border border-gray-200 dark:border-gray-600 rounded-lg p-4 bg-gray-50 dark:bg-gray-800">
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Quick Add Variants
              </label>
              <div className="flex flex-wrap gap-2">
                {Object.entries(PREDEFINED_VARIANT_OPTIONS).map(([optionName, values]) => (
                  <Button
                    key={optionName}
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => handleAddVariantOption(optionName, values)}
                    title={`Add ${optionName}: ${values.join(', ')}`}
                  >
                    {optionName}
                  </Button>
                ))}
              </div>
            </div>

            <div className="border-t border-gray-200 dark:border-gray-600 pt-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Custom Variant
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Input
                  placeholder="Variant name (e.g., Size)"
                  value={customVariantName}
                  onChange={(e) => setCustomVariantName(e.target.value)}
                />
                <Input
                  placeholder="Values (comma separated, e.g., S, M, L)"
                  value={customVariantValues}
                  onChange={(e) => setCustomVariantValues(e.target.value)}
                />
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleAddCustomVariant}
                className="mt-2"
              >
                Add Custom Variant
              </Button>
            </div>
          </div>
        )}

        {/* Variant List */}
        {variants.length > 0 && (
          <div className="space-y-3">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {variants.length} variant{variants.length > 1 ? 's' : ''} created
            </p>
            {variants.map((variant) => (
              <div
                key={variant.id}
                className="border border-gray-200 dark:border-gray-600 rounded-lg p-4 bg-white dark:bg-gray-700"
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h4 className="font-medium text-gray-900 dark:text-white">
                      {variant.options.map((opt) => opt.value).join(' / ')}
                    </h4>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {variant.options.map((opt) => `${opt.name}: ${opt.value}`).join(', ')}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleRemoveVariant(variant.id!)}
                    className="p-1 text-red-600 hover:text-red-700 dark:text-red-400"
                  >
                    <TrashIcon className="h-5 w-5" />
                  </button>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Input
                    label="SKU"
                    value={variant.sku}
                    onChange={(e) => handleVariantChange(variant.id!, 'sku', e.target.value)}
                  />
                  <Input
                    label="Quantity"
                    type="number"
                    value={variant.inventory?.quantity ?? ''}
                    onChange={(e) => handleVariantChange(variant.id!, 'inventory.quantity', e.target.value)}
                    placeholder="0"
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Options */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white">Options</h3>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Status
          </label>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value as any)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
          >
            <option value="active">Active</option>
            <option value="draft">Draft</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>

        <Switch
          label="Featured"
          checked={featured}
          onChange={(e) => setFeatured(e.target.checked)}
        />

        <Switch
          label="Bestseller"
          checked={bestseller}
          onChange={(e) => setBestseller(e.target.checked)}
        />
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-3 pt-6 border-t border-gray-200 dark:border-gray-700">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" loading={isLoading}>
          {product ? 'Update Product' : 'Create Product'}
        </Button>
      </div>

      {/* Image Lightbox */}
      <Lightbox
        open={lightboxOpen}
        close={() => setLightboxOpen(false)}
        index={lightboxIndex}
        slides={[
          ...existingImages.map((img) => ({ src: img.src, alt: img.alt })),
          ...imagePreviews.map((preview, index) => ({ src: preview, alt: `Preview ${index + 1}` })),
        ]}
      />

      {/* Barcode Scanner Modal */}
      <BarcodeScannerModal
        isOpen={isScannerOpen}
        onClose={() => setIsScannerOpen(false)}
        onBarcodeScanned={handleBarcodeScanned}
      />
    </form>
  );
};

export default ProductForm;
